/* eslint-disable complexity */
import PDFDocument from "pdfkit";
import SVGtoPDF from "svg-to-pdfkit";
import Handlebars from "handlebars";
import jpegAutoRotate from "jpeg-autorotate";
import { EncounterPrintable } from "./types/obschart.interfaces";
import {
  PrintableObservationTableSection,
  cellHorizDistance,
  cellVertDistance,
  defaultWidth,
  endOfPageY,
  startOfPageY
} from "./config/chartdef";

// #region misc
export const camelCaseToTitleCase = (str: string) => {
  const result = str.replace(/([A-Z])/g, " $1");
  if ("ph" === result) {
    return ("pH");
  }
  return ((result.charAt(0).toUpperCase() + result.slice(1)).trim());
};

export const capitalizeFirstLetter = (str?: string) => {
  if (!str) {
    return (str);
  }
  return (str.charAt(0).toUpperCase() + str.slice(1));
};

export const getEdgeSpacing = (isOdd: boolean) => {
  return ({
    leftHandEdgeTxt: isOdd ? 67 : 46,
    leftHandEdgeTable: isOdd ? 60 : 39,
  });
};
// #endregion

// #region Helpers

// const file2String = (filePath: string) => {
//   return (fs.readFileSync(filePath, "utf8"));
// };

// const getSvgPath = (svgName: string) => {
//   return (path.join(__dirname, "svg", svgName));
// };

/**
 * Adds an SVG to a PDF document at the specified position.
 *
 * @param doc - The PDF document object.
 * @param svgFileName - The name of the SVG file to add.
 * @param position - The position where the SVG should be added, specified as [x, y] coordinates.
 * @param subs - Optional substitutions to be applied to the SVG template (only applicable if you have any \
 *  `{{mustache}}` tags in your SVG file).
 * @param options - Optional options for converting the SVG to PDF.
 */
export const addSVG = async (
  doc: typeof PDFDocument,
  svgFileName:
    "photo-placeholder-patient.svg" |
    "photo-placeholder-problem.svg" |
    "mev-template-odd.svg" |
    "mev-template-even.svg" |
    "mev-template-p1.svg" |
    "mev-template-obs-odd.svg" |
    "mev-template-obs-even.svg" |
    "mev-template-obs-hypercapnic-zero-odd.svg" |
    "mev-template-obs-hypercapnic-flow-odd.svg" |
    "mev-template-obs-hypercapnic-zero-even.svg" |
    "mev-template-obs-hypercapnic-flow-even.svg" |
    "mev-template-notes-even.svg" |
    "mev-template-notes-odd.svg" |
    "mev-template-investigations-odd.svg" |
    "mev-template-investigations-even.svg" |
    "mev-template-neuro-odd.svg" |
    "mev-template-neuro-even.svg" |
    "test_barcode.svg",
  position: [x: number, y: number],
  subs: any = {},
  options?: SVGtoPDF.SVGtoPDFOptions
) => {

  const svgDataResponse = await fetch(`/svg-chart/${svgFileName}`);
  const svgData = await svgDataResponse.text();

  // Use Handlebars for template substitution (except for barcode)
  if (svgFileName === "test_barcode.svg") {
    SVGtoPDF(doc, svgData, position[0], position[1], options);
  } else {
    const svgTemplate = Handlebars.compile(svgData, {
      preventIndent: true,
    });
    const svg = svgTemplate(subs);
    SVGtoPDF(doc, svg, position[0], position[1], options);
  }
};

/**
 * @returns Either a corrected buffer or the original image base64 URL data
 */
const correctImageOrientation = async (imgData: string) => {

  let retVal: Buffer | string = imgData;
  try {
    const base64Data = (()=> {
      if (imgData.match(/^data:image\/.*;base64,/) !== null) {
        return (imgData.split(",")[1]);
      }
      return (imgData);
    })();
    const imgBuffer = Buffer.from(base64Data, "base64");
    const correctedImage = await jpegAutoRotate.rotate(imgBuffer, {});
    retVal = correctedImage.buffer;
  } catch (e) {
    console.warn("Error correcting image orientation, falling back to original");
  }

  return (retVal);
};

export const addImgData = async (
  doc: typeof PDFDocument,
  imgData: string,
  position: [x: number, y: number],
  fit?: [width: number, height: number],
  drawBorderAroundFit?: boolean,
) => {

  const [x, y] = position;
  if (ImageData) {
    try {
      const autoOrientedImg = await correctImageOrientation(imgData);
      doc.image(
        autoOrientedImg,
        x,
        y,
        fit ? { fit, align: "center", valign: "center" } : undefined,
      );
    } catch (e) {
      console.warn("Error adding image to PDF...continuing...");
    }
  }

  if (fit && drawBorderAroundFit && fit[0] && fit[1]) {
    const [width, height] = fit;
    doc.rect(x, y, width, height).stroke("#000000");
  }
};

// #region layout

export const drawPageTemplate = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  template:
  "odd" |
  "even" |
  "mev-p1" |
  "mev-obschart-odd" |
  "mev-obschart-even" |
  "mev-obschart-hypercapnic-zero-odd" |
  "mev-obschart-hypercapnic-flow-odd" |
  "mev-obschart-hypercapnic-zero-even" |
  "mev-obschart-hypercapnic-flow-even" |
  "mev-notes-odd" |
  "mev-notes-even" |
  "mev-investigations-odd" |
  "mev-investigations-even" |
  "mev-template-neuro-odd" |
  "mev-template-neuro-even",
) => {

  const templateName = (() => {
    switch (template) {
      case "odd":
        return ("mev-template-odd.svg");
      case "even":
        return ("mev-template-even.svg");
      case "mev-p1":
        return ("mev-template-p1.svg");
      case "mev-obschart-odd":
        return ("mev-template-obs-odd.svg");
      case "mev-obschart-even":
        return ("mev-template-obs-even.svg");
      case "mev-obschart-hypercapnic-zero-odd":
        return ("mev-template-obs-hypercapnic-zero-odd.svg");
      case "mev-obschart-hypercapnic-flow-odd":
        return ("mev-template-obs-hypercapnic-flow-odd.svg");
      case "mev-obschart-hypercapnic-zero-even":
        return ("mev-template-obs-hypercapnic-zero-even.svg");
      case "mev-obschart-hypercapnic-flow-even":
        return ("mev-template-obs-hypercapnic-flow-even.svg");
      case "mev-notes-even":
        return ("mev-template-notes-even.svg");
      case "mev-notes-odd":
        return ("mev-template-notes-odd.svg");
      case "mev-investigations-odd":
        return ("mev-template-investigations-odd.svg");
      case "mev-investigations-even":
        return ("mev-template-investigations-even.svg");
      case "mev-template-neuro-odd":
        return ("mev-template-neuro-odd.svg");
      case "mev-template-neuro-even":
        return ("mev-template-neuro-even.svg");
      default:
        return ("mev-template-odd.svg");
    }
  })();

  const isOdd = template === "odd" ||
    template === "mev-obschart-odd" ||
    template === "mev-obschart-hypercapnic-zero-odd" ||
    template === "mev-obschart-hypercapnic-flow-odd" ||
    template === "mev-notes-odd" ||
    template === "mev-investigations-odd" ||
    template === "mev-p1" ||
    template === "mev-template-neuro-odd";
  // on all pages we need to place the forename and surname
  const forename = printData?.patient?.forename || "";
  const surname = printData?.patient?.surname || "";
  const lheNames = isOdd ? 337 : 315;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  await printText(
    doc,
    printData,
    surname,
    [lheNames, 39],
    isOdd,
    { fontSize: 10, fontFamily: "arial", maxTextWidth: 142, elipsizeIfMaxTextWidthExceeded: true, maxTextHeight: 25, ignoreOverflow: true },
  );

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  await printText(
    doc,
    printData,
    forename,
    [lheNames, 61],
    isOdd,
    { fontSize: 10, fontFamily: "arial", maxTextWidth: 222, elipsizeIfMaxTextWidthExceeded: true, maxTextHeight: 25, ignoreOverflow: true },
  );

  await addSVG(
    doc,
    templateName,
    [0, 0],
    printData,
    {
      precision: 100,
    },
  );

};

export const horizLine = (
  doc: typeof PDFDocument,
  pos: [x: number, y: number],
  length: number
) => {
  const [x, y] = pos;
  // doc.moveTo(x, y).lineTo(x + length, y).lineWidth(1).stroke("#000000");
  doc.rect(x, y, length, 0.5).fill("#000000");
};

// #endregion

/**
 *
 * @param doc
 * @param text
 * @param position
 * @param isOdd_ If null, don't do page flipping - just write without protection
 * @param opts
 */
export const printText = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  text: string | string[],
  position: [x: number, y: number],
  isOdd_: boolean,
  opts: {
    fontSize?: number,
    fontFamily?: "arial" | "arial-bold" | "arial-italic" | "arial-bold-italic",
    color?: "red" | "orange" | "blue" | "white" | "black",
    width?: number,
    arrayDelimiter?: string,
    trimAndEllipsize?: number,
    continued?: true,
    ignoreOverflow?: true,
    maxTextHeight?: number,
    elipsizeIfMaxTextHeightExceeded?: boolean,
    maxTextWidth?: number,
    elipsizeIfMaxTextWidthExceeded?: boolean,
  } = {},
) => {

  let isOdd = isOdd_;
  const fontSize = opts?.fontSize || 12;
  const fontFamily = opts?.fontFamily || "arial";
  const width = opts.width ?? defaultWidth;
  const maxTextHeight = opts.maxTextHeight ?? null;
  const maxTextWidth = opts.maxTextWidth ?? null;
  let [x, y] = position;

  let formattedText = (() => {
    if (Array.isArray(text)) {
      return (text.join(`${opts.arrayDelimiter || ","} `));
    }
    return (text as string);
  }
  )();

  if (opts.trimAndEllipsize && opts.trimAndEllipsize > 0 && formattedText.length > opts.trimAndEllipsize) {
    formattedText = (() => {
      if (formattedText.length > opts.trimAndEllipsize) {
        return (`${formattedText.slice(0, opts.trimAndEllipsize)}...`);
      }
      return (formattedText);
    })();
  }

  // if we have a max text height set, we need to remove a character at a time until we're under the limit
  if (maxTextHeight) {
    const startTextLength = formattedText.length;
    let textHeight = doc.heightOfString(formattedText, { width });
    while (textHeight > maxTextHeight) {
      formattedText = formattedText.slice(0, -1);
      textHeight = doc.heightOfString(formattedText, { width });
    }
    if (opts.elipsizeIfMaxTextHeightExceeded && formattedText.length < startTextLength) {
      // the ellipsis is generally only 2 characters wide
      formattedText = `${formattedText.slice(0, -2)}...`;
    }
  }

  // if we have a max text width set, we need to remove a character at a time until we're under the limit
  if (maxTextWidth) {
    const startTextLength = formattedText.length;
    let textWidth = doc.widthOfString(formattedText);
    while (textWidth > maxTextWidth) {
      formattedText = formattedText.slice(0, -1);
      textWidth = doc.widthOfString(formattedText);
    }
    if (opts.elipsizeIfMaxTextWidthExceeded && formattedText.length < startTextLength) {
      // the ellipsis is generally only 2 characters wide
      formattedText = `${formattedText.slice(0, -2)}...`;
    }
  }

  if (!(opts?.ignoreOverflow ?? false)) {
    // test if the text will overflow the page, and if so, print it on the next page if needed
    // we only ever print a paragraph, or line/row at a time, so this strategy is fine
    const containsNewline = formattedText.includes("\n");
    let lineGap = 0;
    if (fontSize >= 12 && containsNewline) {
      // if (containsNewline) {
      // it gets the heights wrong for newlines, so we need to add a line gap
      lineGap = 2.8;
    }
    doc.fontSize(fontSize).font(fontFamily);
    const textHeight = doc.heightOfString(formattedText, { width });
    if ((y + textHeight) >= endOfPageY) {
      console.log("Auto overflow page C891");
      isOdd = !isOdd;
      doc.addPage();
      await drawPageTemplate(doc, printData, isOdd ? "odd" : "even");
      const newEdgeSpacing = getEdgeSpacing(isOdd);
      x = newEdgeSpacing.leftHandEdgeTxt;
      y = startOfPageY;
    }
  }

  doc
    .font(fontFamily)
    .fontSize(fontSize)
    .fillColor(opts.color || "black")
    .text(formattedText, x, y, { width: opts.width, continued: opts.continued });

  return (isOdd);
};

export const printRowOfColumizedText = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  textArr: string[],
  columnPercentages: number[],
  position: [x: number, y: number],
  isOdd_: boolean,
  opts: {
    fontSize?: number,
    fontFamily?: "arial" | "arial-bold" | "arial-italic" | "arial-bold-italic",
    fontFamilies?: Array<"arial" | "arial-bold" | "arial-italic" | "arial-bold-italic">,
    color?: "red" | "orange" | "blue" | "white" | "black",
    width?: number,
    dividerLine?: boolean,
    verticalPadding?: number,
  } = {},
) => {

  let isOdd = isOdd_;
  const fontSize = opts?.fontSize || 12;
  const fontFamily = opts?.fontFamily || "arial";
  const dividerLine = opts?.dividerLine ?? false;
  let [xStart, yStart] = position;
  const width = opts.width ?? defaultWidth;

  if (textArr.length > 0) {

    if (textArr.length !== columnPercentages.length) {
      throw new Error("textArr and columnPercentages must be the same length");
    }

    const columnWidths = columnPercentages.map((p) => {
      if (p <= 1) {
        return (p * width);
      }

      return ((p / 100) * width);
    });

    // check if any of the columns will overflow the page
    const textHeights = textArr.map((text, i) => {
      const columnFontFamily = opts?.fontFamilies?.[i] || fontFamily;

      const containsNewline = text.includes("\n");
      let lineGap = 0;
      if (fontSize >= 12 && containsNewline) {
      // if (containsNewline) {
        // it gets the heights wrong for newlines, so we need to add a line gap
        lineGap = 2.8;
      }

      doc.fontSize(fontSize).font(columnFontFamily);
      return (doc.heightOfString(text, { width: columnWidths[i], lineGap }));
    });
    const tallestColumnHeight = Math.max(...textHeights);
    const isOverflow = (yStart + tallestColumnHeight) >= endOfPageY;
    if (isOverflow) {
      console.log("Auto overflow page C892");
      isOdd = !isOdd;
      doc.addPage();
      await drawPageTemplate(doc, printData, isOdd ? "odd" : "even");
      xStart = getEdgeSpacing(isOdd).leftHandEdgeTxt;
      yStart = startOfPageY;
    }

    // now we're safe to work out our column x positions
    const columnXPositions = columnWidths.reduce((acc, w, i) => {
      if (i === 0) {
        return ([xStart]);
      }
      return ([
        ...acc,
        acc[i - 1] + columnWidths[i - 1],
      ]);
    }, [] as number[]);

    // print each column
    textArr.forEach((text, i) => {
      const columnFontFamily = opts?.fontFamilies?.[i] || fontFamily;
      doc
        .font(columnFontFamily)
        .fontSize(fontSize)
        .fillColor(opts.color || "black")
        .text(text, columnXPositions[i], yStart, { width: columnWidths[i] });
    });

    // move the cursor to the next line
    // doc.moveTo(xStart, yStart + tallestColumnHeight);
    const vPad = opts?.verticalPadding ?? 3;
    doc.y = yStart + tallestColumnHeight + vPad;

    // print the horizontal lines
    if (dividerLine) {
      horizLine(doc, [getEdgeSpacing(isOdd).leftHandEdgeTxt, doc.y], 484);
      doc.y += 3;
    }
  }
  return (isOdd);
};

/**
 * Remember, after pdfkit has finished printing something, it automatically moves the cursor to
 * the next line, so you only need to offset if you want extra space.
 */
export const getDocCurrPosition = (doc: typeof PDFDocument, offset?: [x: number, y: number]) => {
  const [x, y] = offset ?? [0, 0];
  return ([doc.x + x, doc.y + y]);
};

export const getNextYTextPosition = (doc: typeof PDFDocument, vertParaSpacing_?: number) => {
  const vertParaSpacing = vertParaSpacing_ ?? 6;
  return (getDocCurrPosition(doc, [0, vertParaSpacing])[1]);
};

/**
 * Draws a box with text on a PDF document.
 *
 * @param doc - The PDF document object.
 * @param side - The side of the page where the box should be drawn ("odd" or "even").
 * @param yPos - The y-coordinate of the box.
 * @param text - The text to be displayed inside the box.
 *
 * @example `boxWithText(doc, printData, "even", 120, "MESSAGES SENT TO PATIENT");`
 */
export const boxWithText = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  side: "odd" | "even",
  yPos: number,
  text: string
) => {

  const width = 500;
  const height = 20;
  let isOdd = side === "odd";
  let y = yPos;

  const willOverflow = (yPos + height) >= endOfPageY;
  if (willOverflow) {
    console.log("Auto overflow page C893");
    isOdd = !isOdd;
    doc.addPage();
    await drawPageTemplate(doc, printData, isOdd ? "odd" : "even");
    y = startOfPageY - 5; // just go back a bit to flush it against the border
  }

  const x = isOdd ? 58 : 35.9;

  doc.rect(x, y, width, height).fill("#b5b5b5");
  await printText(
    doc,
    {} as any, // don't care about overflowing, so this will never get used
    text,
    [x + 2, y + 1],
    true, // don't care condition
    {
      color: "white",
      fontFamily: "arial-bold",
      fontSize: 16,
      ignoreOverflow: true,
    }
  );

  return (isOdd);
};

export const drawSolidCircle = (doc: typeof PDFDocument, pos: [x: number, y: number], radius: number, color?: string) => {
  const [x, y] = pos;
  doc.circle(x, y, radius).fill(color || "#000000");
};

/**
 * Draws two circles joined with a line.
 *
 * @param doc - The PDFDocument object.
 * @param pos - An array of two positions [x, y] representing the center of each circle.
 * @param radius - The radius of the circles.
 */
export const drawTwoCirclesJoinedWithLine = (doc: typeof PDFDocument, pos: [x: number, y: number][], radius: number) => {
  const [pos1, pos2] = pos;
  const [x1, y1] = pos1;
  const [x2, y2] = pos2;

  doc.circle(x1, y1, radius).fill("#000000");
  doc.circle(x2, y2, radius).fill("#000000");
  // doc.moveTo(x1, y1).lineTo(x2, y2).dash(2, {space: 2}).stroke("#000000");
  doc.moveTo(x1, y1).lineTo(x2, y2).stroke("#000000");
};

// #endregion

// #region obs chart

const getColorFromNews = (news: 0 | 1 | 2 | 3 | 4) => {

  switch (news) {
    case 1:
      return ("#AC7811");
    case 2:
      return ("#B5352F");
    case 3:
      return ("#B5352F");
    default:
      // return ("#262E54"); // no idea why not just straight black?
      return ("#000000");
  }
};

export const findDrawAtCoords = (
  chartDefSection: PrintableObservationTableSection,
  observationNumber: number,
  nudge: [x: number, y: number],
  value: any,
  distances?: [cellHorizDistance: number, cellVertDistance: number],
): [x: number, y: number] => {

  const [nudgeX, nudgeY] = nudge;
  const { pdfStartCoords, inputs } = chartDefSection;
  // find which index in the inputs array the value corresponds to the value lies within the max and min
  const inputRowNumber = (() => {
    // Handle null/undefined values
    if (value === null || value === undefined) {
      return (0);
    }
    const inputRow = inputs.find((input: any, i) => {
      if (input.range && typeof input.range === "object") {
        const { min, max } = input.range;
        return (value >= min && value <= max);
      } else if ("value" in input && input.value !== undefined && input.value === value) {
        return (true);
      }
      return (false);
    });
    if (inputRow) {
      return (inputs.indexOf(inputRow));
    }
    return (0);
  })();

  const drawAtCoord: [x: number, y: number] = [
    pdfStartCoords[0] + (observationNumber * (distances?.[0] || cellHorizDistance)) + nudgeX,
    pdfStartCoords[1] + (inputRowNumber * (distances?.[1] || cellVertDistance)) + nudgeY,
  ];

  // const newsValue = inputs[inputRowNumber].news; // not used

  return (drawAtCoord);
};

export const putDotOnChart = (
  doc: typeof PDFDocument,
  config: PrintableObservationTableSection[],
  nudge: [x: number, y: number],
  distances?: [cellHorizDistance: number, cellVertDistance: number]
) => (dataKey: string, observationNumber: number, value: number, additionalNudge?: [x: number, y: number]) => {

  const section = config.find((s) => s.dataKey === dataKey);
  const [nudgeX1, nudgeY1] = nudge;
  const [nudgeX2, nudgeY2] = additionalNudge ?? [0, 0];
  const nudgeX = nudgeX1 + nudgeX2;
  const nudgeY = nudgeY1 + nudgeY2;
  if (section) {

    const drawAtCoord = findDrawAtCoords(
      section,
      observationNumber,
      [nudgeX, nudgeY],
      value,
      distances,
    );

    const dotColor = "#000000"; // they asked for black dots... // getColorFromNews(newsValue ?? 0);
    drawSolidCircle(doc, drawAtCoord, 1.5, dotColor);
  } else {
    console.error(`could not find section for dataKey: ${dataKey}`);
  }
};

export const putTwoDotsOnChartWithLine = (doc: typeof PDFDocument, config: PrintableObservationTableSection[], nudge: [x: number, y: number]) => (dataKey: string, observationNumber: number, value1: number, value2: number) => {

  const section = config.find((s) => s.dataKey === dataKey);
  const [nudgeX, nudgeY] = nudge;
  if (section) {
    const { pdfStartCoords, inputs } = section;
    // find which index in the inputs array the value corresponds to the value lies within the max and min
    const inputRowNumber1 = (() => {
      const inputRow = inputs.find((input: any, i) => { // TODO don't smash it down to any
        if (input.range && typeof input.range === "object") {
          const { min, max } = input.range;
          return (value1 >= min && value1 <= max);
        }
        return (false);
      });
      if (inputRow) {
        return (inputs.indexOf(inputRow));
      }
      return (0);
    })();
    const inputRowNumber2 = (() => {
      const inputRow = inputs.find((input: any, i) => { // TODO don't smash it down to any
        if (input.range && typeof input.range === "object") {
          const { min, max } = input.range;
          return (value2 >= min && value2 <= max);
        }
        return (false);
      });
      if (inputRow) {
        return (inputs.indexOf(inputRow));
      }
      return (0);
    })();

    const drawAtCoord1: [x: number, y: number] = [
      pdfStartCoords[0] + (observationNumber * cellHorizDistance) + nudgeX,
      pdfStartCoords[1] + (inputRowNumber1 * cellVertDistance) + nudgeY,
    ];
    const drawAtCoord2: [x: number, y: number] = [
      pdfStartCoords[0] + (observationNumber * cellHorizDistance) + nudgeX,
      pdfStartCoords[1] + (inputRowNumber2 * cellVertDistance) + nudgeY,
    ];
    drawTwoCirclesJoinedWithLine(doc, [drawAtCoord1, drawAtCoord2], 1.5);
  } else {
    console.error(`could not find section for dataKey: ${dataKey}`);
  }
};
// #endregion

export const addPageNumber = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  currentPageNumber: number,
  totalPages: number,
  isOdd: boolean
) => {
  const pageNumberText = `Page ${currentPageNumber} of ${totalPages}`;

  // Calculate text width for right alignment
  doc.font("arial").fontSize(9);
  const textWidth = doc.widthOfString(pageNumberText);

  // Use the same layout system as the content
  const edgeSpacing = getEdgeSpacing(isOdd);
  const contentWidth = 484; // Same width as used in horizLine throughout the codebase

  // Add small additional offset for odd pages to align properly with content border
  const oddPageOffset = isOdd ? 22 : 0;

  // Position at bottom right of content area
  const xPosition = edgeSpacing.leftHandEdgeTxt + contentWidth - textWidth + oddPageOffset;
  const yPosition = 822; // Bottom margin area (A4 height is ~842 points)

  await printText(
    doc,
    printData,
    pageNumberText,
    [xPosition, yPosition],
    isOdd,
    {
      fontSize: 9,
      fontFamily: "arial",
      ignoreOverflow: true
    },
  );
};
